import { api, type Quota, type SessionPayload } from './api.ts';
import { t } from './i18n/index.ts';
import { el, mount } from './ui/dom.ts';
import { stateBlock } from './ui/feedback.ts';

type Listener = () => void;

export interface AppState {
  session: SessionPayload | null;
  loaded: boolean;
  quality: number;
  buildId: string;
  updatePending: boolean;
}

const QUALITY_KEY = 'akis.quality';

export const state: AppState = {
  session: null,
  loaded: false,
  quality: readQuality(),
  buildId: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev',
  updatePending: false,
};

declare const __BUILD_ID__: string;

const listeners = new Set<Listener>();

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notify(): void {
  for (const listener of listeners) listener();
}

function readQuality(): number {
  try {
    const saved = Number(localStorage.getItem(QUALITY_KEY));
    return [1080, 720, 480, 360].includes(saved) ? saved : 720;
  } catch {
    return 720; // gizli sekme / depolama kapalı
  }
}

export function setQuality(value: number): void {
  state.quality = value;
  try { localStorage.setItem(QUALITY_KEY, String(value)); } catch { /* depolama yok */ }
}

export async function loadSession(): Promise<void> {
  for (;;) {
    try {
      // /session oturumsuz ziyaretçide de 200 döner; /me'nin 401'i konsola hata düşürüyordu.
      state.session = (await api.get<{ session: SessionPayload | null }>('/api/auth/session')).session;
      break;
    } catch {
      // "Sunucuya ulaşamadım" ile "oturum yok" aynı şey değil. Eskiden ikisi de
      // session = null yapıyordu: zayıf sinyalle açılan araçta geçerli çerezi
      // olan kullanıcı giriş ekranına düşüyordu.
      // Sonradan yapılan tazelemede (oynatıcı kapandı vb.) eldeki oturum korunur.
      if (state.loaded) break;
      // İlk yüklemede yanıt yoksa karar verilemez: beklenir ve yeniden denenir.
      await waitForRetry();
    }
  }
  state.loaded = true;
  notify();
}

/** Açılışta sunucuya ulaşılamadı: açıklama + "Tekrar deneyin". Bağlantı
 *  geri gelince (online olayı) kendiliğinden de dener. */
function waitForRetry(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      window.removeEventListener('online', go);
      const host = document.getElementById('app');
      if (host) mount(host, el('div', { class: 'boot' }, el('div', { class: 'spinner' })));
      resolve();
    };
    const host = document.getElementById('app');
    if (!host) { window.setTimeout(go, 3000); return; }
    mount(host, el('div', { class: 'wrap' }, stateBlock({
      title: t('boot.offlineTitle'),
      body: t('boot.offlineBody'),
      action: { label: t('common.retry'), onClick: go },
    })));
    window.addEventListener('online', go);
  });
}

export function setSession(payload: SessionPayload | null): void {
  state.session = payload;
  notify();
}

export function updateQuota(quota: Quota | undefined): void {
  if (!quota || !state.session) return;
  state.session.quota = quota;
  notify();
}

export function isLoggedIn(): boolean {
  return !!state.session;
}

export function hasIptv(): boolean {
  return !!state.session?.entitlements.iptv;
}

export function planName(): string {
  return state.session?.entitlements.planName ?? t('plans.name.free');
}

/** Yenileme şu an bir şeyi yarıda keser mi? Oynatıcı katmanı DOM'daysa (oturum
 *  henüz açılmamış, "Bağlanıyor…" olsa bile) ya da odak bir form alanındaysa evet. */
function userIsBusy(): boolean {
  try {
    if (document.querySelector('.player')) return true;
    if (document.querySelector('.backdrop')) return true;
    const active = document.activeElement;
    return !!active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName);
  } catch {
    return false;
  }
}

/** Açık kalan bir sayfanın eski derlemede takılmasını önler: yeni derleme varsa
 *  işaretlenir, oynatma bitince yenilenir (oynatmayı ortasından kesmeyiz). */
export function startVersionWatch(isPlaying: () => boolean): void {
  // Derleme numarası artan bir zaman damgası: yalnızca sunucudaki sürüm BİZİMKİNDEN
  // YENİYSE tazeleniyoruz. Eşitsizliğe bakmak, geliştirmede sayfa yeni koda geçip
  // sunucu eski kaldığında sonsuz yeniden yükleme döngüsü üretiyordu.
  const check = async () => {
    try {
      const { build } = await api.get<{ build: string }>('/api/version');
      const theirs = Number(build);
      const ours = Number(state.buildId);
      if (!Number.isFinite(theirs) || !Number.isFinite(ours) || theirs <= ours) return;
      // Yalnız ilk fark edişte haber ver: her turda bildirmek üst çubuğu (ve
      // içindeki arama kutusunu) 90 saniyede bir baştan kuruyordu.
      if (!state.updatePending) {
        state.updatePending = true;
        notify();
      }
      // Tek bir tazelemeyle yetin: sunucu bir sebeple hep daha yeni görünürse
      // sayfa döngüye girmesin.
      let alreadyReloaded = false;
      try { alreadyReloaded = sessionStorage.getItem('akis.reloadedFor') === build; } catch { /* depolama yok */ }
      // Oynatıcı açıkken (bağlanırken de) ya da kullanıcı bir alana yazarken
      // yenileme yapılmaz; sonraki turda (90 sn) yeniden bakılır.
      if (!isPlaying() && !alreadyReloaded && !userIsBusy()) {
        try { sessionStorage.setItem('akis.reloadedFor', build); } catch { /* depolama yok */ }
        location.reload();
      }
    } catch { /* geçici; bir sonraki turda yeniden dene */ }
  };
  setInterval(check, 90_000);
  void check();
}
